import dynamic from 'next/dynamic';
import { useMemo } from 'react';
import PropTypes from 'prop-types';

import { addCartProduct } from '@/redux/slices/cart.slice';
import { addAlert } from '@/redux/slices/primeur.slice';

import { useScreenSizeContext } from '@/components/ScreenSize.context';
import { useAppDispatch, useAppSelector } from '@/redux';
import { getProductModel } from '@/utils/itemSelectorUtils';
import { useFeature } from '@/hooks';

import CallToActionContainer from '@/components/common/callToAction/CallToActionContainer';
import BlocSelector from '@/components/common/ItemSelector/BlocSelector';
import MillesimaPrice from '@/components/common/price/MillesimaPrice';
import Dropdown from '@/components/common/design-system/Dropdown.ds';

const BadgeForAdmin = dynamic(() => import('@/components/product/BadgeForAdmin'), { ssr: false });
BadgeForAdmin.displayName = 'BadgeForAdmin';

import styles from './ItemSelector.module.scss';
import { FEATURES } from '@/constants/features';
import { URLS } from '@/constants/urls';

export const ItemSelector = ({
  t,
  product,
  currentItem,
  onItemChange,
  title,
  isVertical = false,
  customClassName,
  selectorType = 'select',
  addToCartRef,
  quantity,
  setQuantity
}) => {
  const isEc = useAppSelector(({ auth }) => !!auth.loggedUser?.isEc);
  const isDesktop = useScreenSizeContext().isDesktop;
  const primeurUser = useAppSelector(({ primeur }) => primeur.primeurUser);
  const storeConf = useAppSelector(({ root }) => root.storeConf);
  const showAdminBadges = useAppSelector(({ user }) => user.settings.showAdminBadges);
  const dispatch = useAppDispatch();

  const { partnumber, shortdesc, stock } = currentItem;
  const capacity = { value: partnumber, label: shortdesc, stock };
  const productModel = getProductModel(product);
  const isCampaignFeatureEnabled = useFeature(storeConf, FEATURES.PRIMEUR_CAMPAIGN);
  const productToAlert = productModel.isAlert;

  const quantities = useMemo(() => {
    return stock ? Array(stock).fill(1).map((_, i) => ({ value: i + 1, label: i + 1 })) : [];
  }, [partnumber]);

  const addToCart = () => {
    dispatch(addCartProduct({
      item: partnumber,
      qty: parseInt(quantity),
      options: {
        set: false,
      },
      error: undefined
    }));
    // Reset to initial value
    setQuantity(1);
  };

  const formatOption = ({ listPrice, offerPrice, shortdesc, partnumber }) => {
    const priceToFormat = offerPrice > 0 ? offerPrice : listPrice;

    const price = MillesimaPrice.format(t, {
      value: priceToFormat,
      locale: storeConf.locale,
      currency: storeConf.currencyISO
    });

    return {
      value: partnumber,
      label: `${shortdesc} - ${price}`
    };
  };

  // The product is not buyable
  // eslint-disable-next-line camelcase
  const unBuyableProduct = (product?.exclusive_cellar && !isEc) || !productModel || !currentItem ||
  (productToAlert && !isCampaignFeatureEnabled);

  const noItems = productModel.itemsList.length === 0;
  const productNoStock = !product.display || noItems || currentItem?.stock === 0;

  let buttonLabel: any = t('product.addToCart');

  let buttonProps: any = {
    onClick: addToCart,
    id: 'add-to-cart',
    buttonRef: addToCartRef
  };

  if (productToAlert) {
    const isAlerted = primeurUser?.alerts?.includes(product?.partnumber);
    buttonLabel = isAlerted ? t('alert.unsubscribe') : t('alert.alertMe');
    buttonProps = {
      id: 'alert-me',
      kind: isAlerted ? 'secondary' : 'primary',
      onClick: () => dispatch(addAlert({
        alerts: [product?.partnumber],
        name: product?.productName || product?.name,
        isAllChecked: undefined,
        storeId: storeConf.storeId
      })),
      icon: {
        url: isAlerted ? URLS.CROSS_BLACK : URLS.NOTIFICATION_WHITE,
        alt: 'alert'
      },
      fullWidth: true
    };
  } else if (productNoStock) {
    buttonLabel = t('product.noStock');
    buttonProps = {
      id: 'no-stock',
      disabled: true,
    };
  }

  const containerClassName = [
    styles.container,
    customClassName
  ].filter(Boolean).join(' ');

  const contentClassName = [
    styles.content,
    (isVertical || !isDesktop) && styles.column
  ].filter(Boolean).join(' ');

  const dropdownsClassName = [
    styles.dropdowns,
    !isDesktop && styles['mobile-device']
  ].filter(Boolean).join(' ');

  const dropdownClassName = [
    styles['qty-button'],
    (productNoStock || productToAlert) && styles['no-stock']
  ].filter(Boolean).join(' ');

  const blocSelectorWithoutProductToAlert = !productToAlert && product.display && selectorType === 'bloc';

  const getButton = () => (
    <CallToActionContainer {...buttonProps} className={styles['millesima-btn']} sensefuelId="add-to-cart" fullWidth>
      {buttonLabel}
    </CallToActionContainer>
  );

  const getDropdownOptions = () => {
    return productModel.itemsList.filter(({ offerPrice, listPrice }) => listPrice > 0 || offerPrice > 0).map(formatOption);
  };

  return !unBuyableProduct ? (
    <div className={containerClassName} data-test-id="item-selector" data-rbf="item-selector">
      {selectorType === 'bloc' && (
        <>
          {blocSelectorWithoutProductToAlert && (
            <BlocSelector
              t={t}
              locale={storeConf.locale}
              currencyISO={storeConf.currencyISO}
              items={productModel.itemsList}
              itemSelected={capacity}
              onSelect={onItemChange}
              isMobileOrTablet={!isDesktop}
            />
          )}
          <div className={dropdownClassName}>
            {currentItem?.stock > 0 && quantities.length > 0 && blocSelectorWithoutProductToAlert && (
              <Dropdown
                id="item-selector"
                value={quantity}
                options={quantities}
                onChange={setQuantity}
              />
            )}
            {getButton()}
          </div>
        </>
      )}
      {selectorType === 'select' && (
        <>
          {title && <div className={styles.title}>{title}</div>}
          <div className={contentClassName} data-test-id="item-selector-content" data-rbf="item-selector-content">
            {!productToAlert && product.display && (
              <div className={dropdownsClassName} data-test-id="item-selector-dropdowns" data-rbf="item-selector-dropdowns">
                <Dropdown
                  id="item-selector"
                  value={capacity?.value}
                  options={getDropdownOptions()}
                  onChange={(value) => onItemChange(value)}
                  isReadOnly={productModel.itemsList.length <= 1}
                />

                {currentItem?.stock > 0 && quantities.length > 0 && (
                  <div className={styles['quantity-dropdown']}>
                    <Dropdown
                      id="quantity"
                      value={quantity}
                      options={quantities}
                      onChange={setQuantity}
                    />
                  </div>
                )}
              </div>
            )}

            {getButton()}
          </div>
        </>
      )}
      {currentItem?.stock > 0 && showAdminBadges && (
        <div className="d-flex" data-rbf="badge-stock-marge">
          <BadgeForAdmin item={currentItem} />
        </div>
      )}
    </div>
  ) : (<></>);
};

ItemSelector.propTypes = {
  t: PropTypes.func.isRequired,
  product: PropTypes.object.isRequired,
  currentItem: PropTypes.object.isRequired,
  onItemChange: PropTypes.func.isRequired,
  title: PropTypes.string,
  isVertical: PropTypes.bool,
  customClassName: PropTypes.string,
  selectorType: PropTypes.oneOf(['bloc', 'select']),
  addToCartRef: PropTypes.object,
  quantity: PropTypes.shape({
    value: PropTypes.number,
    label: PropTypes.number
  }).isRequired,
  setQuantity: PropTypes.func.isRequired
};

export default ItemSelector;
